import React from 'react';
import { UseFieldArrayAppend, UseFieldArrayRemove, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Radio } from '../../../../../CommonComponents/OPL/Radio/Radio';
import { EMPTY_BUCKET, FormFieldValues } from '../../AssetBucketing.meta';
import TwoColumnsSVG from '../../../../../../img/AssetBucketing/2columns.svg';
import ThreeColumnsSVG from '../../../../../../img/AssetBucketing/3columns.svg';
import FourColumnsSVG from '../../../../../../img/AssetBucketing/4columns.svg';
import { ClassificationDTO } from '../../api/useAssetBucketQuery';

const OPTIONS = [
  {
    label: '2 Columns',
    value: 2,
    Illustration: TwoColumnsSVG,
  },
  {
    label: '3 Columns',
    value: 3,
    Illustration: ThreeColumnsSVG,
  },
  {
    label: '4 Columns',
    value: 4,
    Illustration: FourColumnsSVG,
  },
];

interface Props {
  appendBucket: UseFieldArrayAppend<FormFieldValues>;
  removeBucket: UseFieldArrayRemove;
}

export const PageLayoutForm: React.FC<Props> = ({ appendBucket, removeBucket }) => {
  const { watch, setValue } = useFormContext<FormFieldValues>();

  const buckets = watch('buckets');
  const unassignedClassifications = watch('unassignedClassifications');

  return (
    <BucketCountForm
      bucketCount={buckets.length}
      onChange={value => {
        setValue('headline', '');

        const bucketCountDiff = value - buckets.length;

        if (bucketCountDiff > 0) {
          appendBucket(
            Array.from(Array(bucketCountDiff), () => EMPTY_BUCKET),
            { shouldFocus: false },
          );
        }

        if (bucketCountDiff < 0) {
          const bucketIndexesToRemove = Array.from(
            Array(Math.abs(bucketCountDiff)),
            (_, i) => i + value,
          );

          const classificationsRemoved = bucketIndexesToRemove
            .map(i => buckets[i])
            .reduce(
              (acc, bucket) => [...acc, ...bucket.classifications],
              [] as ClassificationDTO[],
            );

          setValue('unassignedClassifications', [
            ...unassignedClassifications,
            ...classificationsRemoved,
          ]);

          removeBucket(bucketIndexesToRemove);
        }
      }}
    />
  );
};

interface BucketCountFormProps {
  bucketCount: number;
  onChange: (value: number) => void;
}

export const BucketCountForm = ({ bucketCount, onChange }: BucketCountFormProps) => {
  return (
    <Box display="flex" role="radiogroup" aria-label="Page layout" mt="24" css="gap: 32px">
      {OPTIONS.map(({ label, value, Illustration }) => {
        const checked = value === bucketCount;

        return (
          <Box
            key={value}
            display="flex"
            flexDirection="column"
            alignItems="center"
            css="gap: 16px"
          >
            <Illustration />

            <Radio checked={checked} onChange={() => onChange(value)}>
              {label}
            </Radio>
          </Box>
        );
      })}
    </Box>
  );
};
